import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Lang, ThemeProvider, Toaster, ToasterComponent, ToasterProvider, configure } from '@gravity-ui/uikit';
import './styles/app.css';
import { createSessionStore } from './lib/session/store.js';
import { App } from './app/app.js';

/**
 * Точка входа платформенной админки.
 *
 * Ни локальной базы, ни выбора клиники — сотрудник Odonix не работает в одной
 * клинике. Сессия — токен в памяти и refresh в cookie. Service worker только
 * в собранной версии: без него телефон не ставит админку как приложение.
 *
 * Интерфейс — Gravity UI, язык консоли Yandex Cloud; подписи самих
 * компонентов («Свернуть», «Закрыть») — по-русски.
 */

configure({ lang: Lang.Ru });

const container = document.getElementById('root');
if (container === null) throw new Error('Не найден корневой элемент');

const session = createSessionStore({ baseUrl: apiBaseUrl() });
const toaster = new Toaster();

createRoot(container).render(
  <StrictMode>
    <ThemeProvider theme="light" lang={Lang.Ru}>
      <ToasterProvider toaster={toaster}>
        <App session={session} />
        <ToasterComponent />
      </ToasterProvider>
    </ThemeProvider>
  </StrictMode>,
);

/**
 * Адрес API. По умолчанию — тот же источник, что и страница: локально это
 * прокси `vite.config.ts` на `platform-admin`, а в облаке — тот же домен,
 * что раздаёт саму админку.
 */
registerAppServiceWorker();

function registerAppServiceWorker(): void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  const register = (): void => {
    void navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' });
  };
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}

function apiBaseUrl(): string {
  const configured: unknown = import.meta.env.VITE_API_BASE_URL;
  return typeof configured === 'string' && configured !== '' ? configured : window.location.origin;
}
